import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { NUVEI_SDK_URL, NUVEI_FIELD_IDS, NUVEI_FIELD_STYLES, getNuveiLocale } from '@/constants/nuvei';
import type { SafeChargeFields, SafeChargeField, SafeChargeInstance } from '@/types/nuvei';
import { getNuveiCardToken, getNuveiCardDetails } from '@/utils/nuvei';
import { useTranslate } from '@/hooks/useTranslate';

interface UseNuveiReturn {
  isLoading: boolean;
  error: string | null;
  fieldsError: {
    cardNumber: string | null;
    expiration: string | null;
    cvc: string | null;
  };
  fieldsRef: React.RefObject<{
    cardNumber: SafeChargeField | null;
    expiration: SafeChargeField | null;
    cvc: SafeChargeField | null;
  }>;
  safeChargeInstance: SafeChargeInstance | null;
  isFieldsValid: boolean;
  saveNuveiCard: (
    sessionToken: string,
    cardholderName: string,
    email: string,
    paymentMethodId: string
  ) => Promise<{
    id: string;
    bin: string;
    cardLastFourDigits: string;
    brand: string;
    cardNumber: string;
    expiryMonth: string;
    expiryYear: string;
    cardholderName: string;
    pspCardIdentifier: string;
    paymentProvider: string;
  } | null>;
}

const isNuveiSDKLoaded = () => typeof window !== 'undefined' && typeof window.SafeCharge !== 'undefined';

const areFieldsAttached = () => {
  const cardNumberEl = document.getElementById(NUVEI_FIELD_IDS.CARD_NUMBER);
  return cardNumberEl?.querySelector('iframe') !== null;
};

export const useNuvei = (): UseNuveiReturn => {
  const {
    paymentKeys,
    card: { setCardElementRef, cardElementRef },
    customer,
  } = useContext(CheckoutContext);
  const nuveiConfig = paymentKeys.nuvei;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldsError, setFieldsError] = useState({
    cardNumber: null as string | null,
    expiration: null as string | null,
    cvc: null as string | null,
  });
  const { t } = useTranslate();
  const [safeChargeInstance, setSafeChargeInstance] = useState<SafeChargeInstance | null>(null);
  const [fieldsValid, setFieldsValid] = useState({
    cardNumber: false,
    expiration: false,
    cvc: false,
  });
  const previousFieldsValid = useRef({
    cardNumber: false,
    expiration: false,
    cvc: false,
  });
  const activeFieldRef = useRef<'cardNumber' | 'expiration' | 'cvc' | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialized = useRef(false);
  const nuveiCardElementRef = useRef<SafeChargeFields | null>(null);
  const fieldsRef = useRef<{
    cardNumber: SafeChargeField | null;
    expiration: SafeChargeField | null;
    cvc: SafeChargeField | null;
  }>({
    cardNumber: null,
    expiration: null,
    cvc: null,
  });

  const isFieldsValid = useMemo(() => {
    return Object.values(fieldsValid).every((valid) => valid);
  }, [fieldsValid]);

  const getNuveiFieldError = (errorMessage: string | undefined, fallbackMessage: string) => {
    if (!errorMessage) {
      return fallbackMessage;
    }
    return t(`fieldErrors.${errorMessage}`);
  };

  const waitForDOMElements = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        const cardNumberEl = document.getElementById(NUVEI_FIELD_IDS.CARD_NUMBER);
        const expirationEl = document.getElementById(NUVEI_FIELD_IDS.EXPIRATION);
        const cvcEl = document.getElementById(NUVEI_FIELD_IDS.CVC);

        if (cardNumberEl && expirationEl && cvcEl) {
          clearInterval(interval);
          resolve(true);
        }

        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          resolve(false);
        }
      }, 100);
    });
  }, []);

  const initializeNuveiFields = useCallback(
    (safeCharge: ReturnType<typeof window.SafeCharge>) => {
      if (areFieldsAttached()) {
        return;
      }

      const safeChargeFields = safeCharge.fields({
        fonts: [{ cssUrl: '' }],
        locale: getNuveiLocale(),
        i18n: {
          cc_card_number: t('cardNumber'),
          cc_expiry: t('expiration'),
          cc_cvv: t('cvc'),
          card_number: t('cardNumber'),
          expiry_date: t('expiration'),
          cvv: t('cvc'),
        },
      });

      const cardNumber = safeChargeFields.create('ccNumber', {
        style: NUVEI_FIELD_STYLES,
      });
      cardNumber.attach(`#${NUVEI_FIELD_IDS.CARD_NUMBER}`);

      const handleCardNumberValidation = (data: unknown, isComingFromBlur: boolean = false) => {
        const eventData = data as {
          complete?: boolean;
          field?: string;
          fieldData?: { ccCardNumber?: string; bin?: string; valueIsChanged?: boolean };
          optional?: boolean;
          error?: { id: string; message: string };
        };
        // Field is valid when complete is true
        const isComplete = eventData.complete ?? false;
        const hasValue = Boolean(eventData.fieldData?.ccCardNumber && eventData.fieldData.ccCardNumber.length > 0);
        const fieldValid = isComplete || hasValue;
        if (isComingFromBlur) {
          const errorMessage = getNuveiFieldError(eventData.error?.message, t('fieldErrors.invalidCard'));
          setFieldsError((prev) => ({
            ...prev,
            cardNumber: fieldValid ? null : errorMessage,
          }));
        }
        setFieldsValid((prev) => {
          previousFieldsValid.current = { ...prev, cardNumber: Boolean(fieldValid) };
          return {
            ...prev,
            cardNumber: Boolean(fieldValid),
          };
        });
        if (isComplete && !isComingFromBlur) {
          setTimeout(() => fieldsRef.current?.expiration?.focus?.(), 0);
        }
      };

      const handleCardNumberBlur = (data: unknown) => {
        handleCardNumberValidation(data, true);
        // Delay clearing active field to allow focus transition to next field
        blurTimeoutRef.current = setTimeout(() => {
          if (activeFieldRef.current === 'cardNumber') {
            activeFieldRef.current = null;
          }
        }, 100);
      };

      cardNumber.on('change', handleCardNumberValidation);
      cardNumber.on('blur', handleCardNumberBlur);
      cardNumber.on('focus', () => {
        // Clear any pending blur timeout to keep keyboard open
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        activeFieldRef.current = 'cardNumber';
      });

      const cardExpiration = safeChargeFields.create('ccExpiration', {
        style: NUVEI_FIELD_STYLES,
      });
      cardExpiration.attach(`#${NUVEI_FIELD_IDS.EXPIRATION}`);

      const handleExpirationValidation = (data: unknown, isComingFromBlur: boolean = false) => {
        const eventData = data as {
          complete?: boolean;
          field?: string;
          fieldData?: { expiryDate?: string; valueIsChanged?: boolean };
          optional?: boolean;
          error?: { id: string; message: string };
        };
        const isComplete = eventData.complete ?? false;
        const hasValue = Boolean(eventData.fieldData?.expiryDate && eventData.fieldData.expiryDate.length > 0);
        const fieldValid = isComplete || hasValue;

        if (isComingFromBlur) {
          const errorMessage = getNuveiFieldError(eventData.error?.message, t('fieldErrors.invalidExpirationDate'));
          setFieldsError((prev) => ({
            ...prev,
            expiration: fieldValid ? null : errorMessage,
          }));
        }
        setFieldsValid((prev) => {
          previousFieldsValid.current = { ...prev, expiration: Boolean(fieldValid) };
          return {
            ...prev,
            expiration: Boolean(fieldValid),
          };
        });
        if (isComplete && !isComingFromBlur) {
          setTimeout(() => fieldsRef.current?.cvc?.focus?.(), 0);
        }
      };

      const handleExpirationBlur = (data: unknown) => {
        handleExpirationValidation(data, true);
        // Delay clearing active field to allow focus transition to next field
        blurTimeoutRef.current = setTimeout(() => {
          if (activeFieldRef.current === 'expiration') {
            activeFieldRef.current = null;
          }
        }, 100);
      };

      cardExpiration.on('change', handleExpirationValidation);
      cardExpiration.on('blur', handleExpirationBlur);
      cardExpiration.on('focus', () => {
        // Clear any pending blur timeout to keep keyboard open
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        activeFieldRef.current = 'expiration';
      });

      const cardCvc = safeChargeFields.create('ccCvc', {
        style: NUVEI_FIELD_STYLES,
      });
      cardCvc.attach(`#${NUVEI_FIELD_IDS.CVC}`);

      const handleCvcValidation = (data: unknown, isComingFromBlur: boolean = false) => {
        const eventData = data as {
          complete?: boolean;
          field?: string;
          fieldData?: { cvv?: string; valueIsChanged?: boolean };
          optional?: boolean;
          error?: { id: string; message: string };
        };
        const isComplete = eventData.complete ?? false;
        const hasValue = Boolean(eventData.fieldData?.cvv && eventData.fieldData.cvv.length > 0);
        const fieldValid = isComplete || hasValue;
        if (isComingFromBlur) {
          const errorMessage = getNuveiFieldError(eventData.error?.message, t('fieldErrors.invalidSecurityCode'));
          setFieldsError((prev) => ({
            ...prev,
            cvc: fieldValid ? null : errorMessage,
          }));
        }
        setFieldsValid((prev) => ({
          ...prev,
          cvc: Boolean(fieldValid),
        }));
      };

      const handleCvcBlur = (data: unknown) => {
        handleCvcValidation(data, true);
        // Delay clearing active field to allow focus transition to next field
        blurTimeoutRef.current = setTimeout(() => {
          if (activeFieldRef.current === 'cvc') {
            activeFieldRef.current = null;
          }
        }, 100);
      };

      cardCvc.on('change', handleCvcValidation);
      cardCvc.on('blur', handleCvcBlur);
      cardCvc.on('focus', () => {
        // Clear any pending blur timeout to keep keyboard open
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        activeFieldRef.current = 'cvc';
      });

      fieldsRef.current = {
        cardNumber,
        expiration: cardExpiration,
        cvc: cardCvc,
      };

      nuveiCardElementRef.current = { ...safeChargeFields, cardObj: cardNumber };
      setCardElementRef({ current: { ...nuveiCardElementRef.current, safeChargeInstance: safeCharge } });
    },
    [setCardElementRef]
  );

  const loadNuveiSDK = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (isNuveiSDKLoaded()) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src="${NUVEI_SDK_URL}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Nuvei SDK')));
        return;
      }

      const script = document.createElement('script');
      script.src = NUVEI_SDK_URL;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Nuvei SDK'));

      document.body.appendChild(script);
    });
  }, []);

  const initializeNuvei = useCallback(async () => {
    if (!nuveiConfig?.merchantId || !nuveiConfig?.merchantSiteId) {
      setError(t('somethingWentWrong'));
      setIsLoading(false);
      return;
    }

    if (isInitialized.current) {
      setIsLoading(false);
      return;
    }

    try {
      const domReady = await waitForDOMElements();
      if (!domReady) {
        setError(t('somethingWentWrong'));
        setIsLoading(false);
        return;
      }

      await loadNuveiSDK();

      const safeCharge = window.SafeCharge({
        merchantId: nuveiConfig.merchantId,
        merchantSiteId: nuveiConfig.merchantSiteId,
        env: nuveiConfig.env || 'int',
      });

      setSafeChargeInstance(safeCharge);
      initializeNuveiFields(safeCharge);
      isInitialized.current = true;
      setIsLoading(false);
    } catch (err) {
      console.error('Nuvei initialization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize Nuvei');
      setIsLoading(false);
    }
  }, [nuveiConfig, loadNuveiSDK, initializeNuveiFields, waitForDOMElements]);

  useEffect(() => {
    initializeNuvei();

    return () => {
      nuveiCardElementRef.current = null;
      fieldsRef.current = {
        cardNumber: null,
        expiration: null,
        cvc: null,
      };
    };
  }, [initializeNuvei]);

  const saveNuveiCard = useCallback(
    async (sessionToken: string, cardholderName: string, email: string, paymentMethodId: string) => {
      if (!safeChargeInstance) {
        return null;
      }

      // Get the card number field - prefer cardElementRef from context (same as payment handler uses)
      let cardNumberField: SafeChargeField | null = null;

      // First try to get from cardElementRef stored in context (this is what payment handler uses)
      if (cardElementRef?.current) {
        const safeChargeFields = cardElementRef.current as SafeChargeFields & {
          safeChargeInstance?: SafeChargeInstance;
        };
        if (safeChargeFields?.cardObj) {
          cardNumberField = safeChargeFields.cardObj as SafeChargeField;
        }
      }

      // Fallback to fieldsRef
      if (!cardNumberField) {
        cardNumberField = fieldsRef.current.cardNumber;
      }

      // Last fallback to stored ref
      if (!cardNumberField && nuveiCardElementRef.current?.cardObj) {
        cardNumberField = nuveiCardElementRef.current.cardObj as SafeChargeField;
      }

      if (!cardNumberField) {
        console.error('Nuvei card field not found');
        return null;
      }

      try {
        const country = (customer?.geoLocator?.countryCode2 ?? customer?.taxCountry ?? '') as string;

        // Get card token using utility function
        const tokenResponse = await getNuveiCardToken(
          safeChargeInstance,
          cardNumberField,
          sessionToken,
          cardholderName,
          email,
          country
        );

        if (!tokenResponse) {
          return null;
        }

        // Get card details using utility function
        const cardDetailsResponse = await getNuveiCardDetails(safeChargeInstance, sessionToken);

        if (!cardDetailsResponse) {
          return null;
        }

        if (cardElementRef?.current) {
          const safeChargeFields = cardElementRef.current as SafeChargeFields;
          safeChargeFields.cardDetails = cardDetailsResponse;
          safeChargeFields.sessionToken = sessionToken;
        }

        const cardDetails = {
          bin: cardDetailsResponse.bin || '',
          last4Digits: cardDetailsResponse.last4digits || '',
          cardType: cardDetailsResponse.cardType || '',
          expiryMonth: cardDetailsResponse.ccExpMonth || '',
          expiryYear: cardDetailsResponse.ccExpYear || '',
        };

        return {
          id: `${paymentMethodId}-${cardDetails.last4Digits || 'card'}-${Date.now()}`,
          bin: cardDetails.bin || '',
          cardLastFourDigits: cardDetails.last4Digits || '',
          brand: (cardDetails.cardType || '').toLowerCase(),
          cardNumber: `${cardDetails.bin || ''}****${cardDetails.last4Digits || ''}`,
          expiryMonth: cardDetails.expiryMonth || '',
          expiryYear: cardDetails.expiryYear || '',
          cardholderName,
          pspCardIdentifier: tokenResponse.ccTempToken,
          paymentProvider: 'nuvei',
          tokenResponse: {
            ccTempToken: tokenResponse.ccTempToken,
            status: tokenResponse.status,
            uniqueCC: tokenResponse.uniqueCC,
            version: tokenResponse.version,
          },
          cardDetails: {
            bin: cardDetailsResponse.bin,
            last4digits: cardDetailsResponse.last4digits,
            cardType: cardDetailsResponse.cardType,
            ccExpMonth: cardDetailsResponse.ccExpMonth,
            ccExpYear: cardDetailsResponse.ccExpYear,
            brand: cardDetailsResponse.brand,
          },
          sessionToken,
        };
      } catch (error) {
        console.error('Error saving Nuvei card:', error);
        return null;
      }
    },
    [safeChargeInstance, fieldsRef, customer, cardElementRef]
  );

  return {
    isLoading,
    fieldsError,
    error,
    fieldsRef,
    safeChargeInstance,
    isFieldsValid,
    saveNuveiCard,
  };
};
